import * as React from 'react';
import { useTranslation } from 'react-i18next';

import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { UniversalCombobox } from '@/components/common/UniversalCombobox';
import { useTeamMutations } from '../hooks';
import type { Matter } from '../types';

interface AddStaffDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  matter: Matter | null;
  onSuccess?: () => void;
}

// Case 360° (Stage 14) — Lead/Partner/MD adds a Contributor (staff- or
// lead-type user; the server validates eligibility and membership).
export function AddStaffDialog({
  open,
  onOpenChange,
  matter,
  onSuccess,
}: AddStaffDialogProps) {
  const { t } = useTranslation();
  const [userId, setUserId] = React.useState<number | undefined>(undefined);
  const { addStaffMutation } = useTeamMutations();

  React.useEffect(() => {
    if (open) setUserId(undefined);
  }, [open]);

  const handleConfirm = async () => {
    if (!matter || !userId) return;
    await addStaffMutation.mutateAsync({ id: matter.id, userId });
    onOpenChange(false);
    onSuccess?.();
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('matterTeam.addMemberTitle')}</AlertDialogTitle>
          <AlertDialogDescription>
            {t('matterTeam.addMemberDescription')}
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="my-4">
          <label className="mb-2 block text-sm font-medium">
            {t('matterTeam.contributors')}
          </label>
          <UniversalCombobox
            url="/users"
            listUrl="/users/select-box"
            labelKey="name"
            valueKey="id"
            selectedId={userId}
            params={{ status: 'ACTIVE' }}
            onSelectItem={(item) =>
              setUserId(item?.['id'] as number | undefined)
            }
            placeholder={t('matterTeam.selectUser')}
          />
          <p className="mt-1 text-xs text-slate-400">
            {t('matterTeam.addMemberHint')}
          </p>
        </div>

        <AlertDialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={addStaffMutation.isPending}
          >
            {t('cancel')}
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!userId || addStaffMutation.isPending}
          >
            {addStaffMutation.isPending ? t('processing') : t('matterTeam.addConfirm')}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
